import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Typography, Avatar, Stack, Chip, Button, Divider, Alert } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/es';

import PersonAddIcon from '@mui/icons-material/PersonAdd';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';

import { handleInvitation, markAllAsRead, getNotifications } from '../../actions/notification';
import { startChat } from '../../actions/chat';
import { nombreDe } from '../../components/common/imparte';
import { TICKET_LINK_TYPES, rutaReserva, botonAviso } from '../../utils/avisos';

moment.locale('es');

const BOOKING_TYPES = ['BOOKING_REQUEST', 'BOOKING_CONFIRMED', 'BOOKING_DECLINED', 'BOOKING_EXPIRED', 'BOOKING_CANCELLED',
  'BOOKING_MODIFICATION_REQUEST', 'BOOKING_MODIFICATION_ACCEPTED', 'BOOKING_MODIFICATION_REJECTED', 'BOOKING_MODIFIED'];

// Botón al lugar exacto de un aviso que no trae el suyo: la reserva de la que habla o «Datos para pagos» (ver utils/avisos)
const BotonAlLugar = ({ notif, navigate }) => {
  const boton = botonAviso(notif);
  if (!boton) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Button
        variant="contained"
        size="small"
        onClick={() => navigate(boton.ruta)}
        sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: 1, fontSize: '11px', '&:hover': { bgcolor: 'primary.dark' } }}
      >
        {boton.texto}
      </Button>
    </Box>
  );
};

const getAvatarColor = (type) => {
  if (type === 'INVITATION_REJECTED' || type === 'BOOKING_DECLINED' || type === 'BOOKING_CANCELLED' || type === 'BOOKING_MODIFICATION_REJECTED') return 'error.main';
  if (type === 'BOOKING_CONFIRMED' || type === 'BOOKING_MODIFICATION_ACCEPTED') return 'success.main';
  if (type === 'BOOKING_MODIFICATION_REQUEST') return 'warning.main';
  if (type === 'BOOKING_MODIFIED' || type === 'BOOKING_DISPUTA_RESUELTA' || type === 'TICKET_RESPUESTA' || type === 'TICKET_ACTUALIZADO') return 'info.main';
  if (type === 'BOOKING_DISPUTA' || type === 'PAYMENT_FAILED' || type === 'PAYMENT_CHARGEBACK' || type === 'SOLICITUD_NO_PROCEDE') return 'error.main';
  if (type === 'BOOKING_AUTOCOMPLETADA' || type === 'PAYMENT_CONFIRMED' || type === 'PAYMENT_RELEASED' || type === 'SOLICITUD_RECIBIDA' || type === 'SOLICITUD_RESUELTA') return 'success.main';
  if (type === 'SOLICITUD_NOVEDAD') return 'warning.main';
  if (type === 'BOOKING_EXPIRED') return 'text.secondary';
  return 'primary.main';
};

const getIcon = (type) => {
  if (type === 'PAGE_INVITATION') return <PersonAddIcon fontSize="small" />;
  if (type === 'BOOKING_REQUEST') return <CalendarMonthIcon fontSize="small" />;
  if (type === 'BOOKING_CONFIRMED') return <EventAvailableIcon fontSize="small" />;
  if (type === 'BOOKING_DECLINED' || type === 'BOOKING_CANCELLED' || type === 'BOOKING_MODIFICATION_REJECTED') return <EventBusyIcon fontSize="small" />;
  if (type === 'BOOKING_MODIFICATION_REQUEST' || type === 'BOOKING_MODIFICATION_ACCEPTED' || type === 'BOOKING_MODIFIED') return <EventAvailableIcon fontSize="small" />;
  if (type === 'BOOKING_EXPIRED') return <AccessTimeIcon fontSize="small" />;
  if (type === 'SOLICITUD_RECIBIDA' || type === 'SOLICITUD_RESUELTA') return <CheckCircleOutlineIcon fontSize="small" />;
  if (type === 'SOLICITUD_NOVEDAD') return <WarningAmberIcon fontSize="small" />;
  if (type === 'SOLICITUD_NO_PROCEDE') return <BlockIcon fontSize="small" />;
  if (type === 'TICKET_RESPUESTA' || type === 'TICKET_ACTUALIZADO') return <ChatBubbleOutlineOutlinedIcon fontSize="small" />;
  if (type === 'COTIZACION_SOLICITADA') return <RequestQuoteOutlinedIcon fontSize="small" />;
  return <NotificationsNoneIcon fontSize="small" />;
};

const getTitle = (type) => {
  switch (type) {
    case 'PAGE_INVITATION': return 'Solicitud para ser facilitador';
    case 'INVITATION_ACCEPTED': return '¡Invitación Aceptada!';
    case 'INVITATION_REJECTED': return 'Invitación Rechazada';
    case 'BOOKING_REQUEST': return 'Nueva solicitud de reserva';
    case 'BOOKING_CONFIRMED': return '¡Reserva confirmada!';
    case 'BOOKING_DECLINED': return 'Reserva declinada';
    case 'BOOKING_CANCELLED': return 'Reserva cancelada por el cliente';
    case 'BOOKING_MODIFICATION_REQUEST': return 'Solicitud de cambio de fecha';
    case 'BOOKING_MODIFICATION_ACCEPTED': return 'Cambio de fecha aceptado';
    case 'BOOKING_MODIFICATION_REJECTED': return 'Cambio de fecha rechazado';
    case 'BOOKING_MODIFIED': return 'Fecha de reserva actualizada';
    case 'BOOKING_EXPIRED': return 'Reserva caducada';
    case 'BOOKING_AUTOCOMPLETADA': return 'Servicio completado';
    case 'BOOKING_DISPUTA': return 'Problema reportado';
    case 'BOOKING_DISPUTA_RESUELTA': return 'Disputa resuelta';
    case 'SOLICITUD_RECIBIDA': return 'Solicitud recibida';
    case 'SOLICITUD_RESUELTA': return 'Solicitud resuelta';
    case 'SOLICITUD_NOVEDAD': return 'Noticias sobre tu solicitud';
    case 'SOLICITUD_NO_PROCEDE': return 'Tu solicitud no procedió';
    case 'TICKET_RESPUESTA': return 'Respuesta a tu ticket';
    case 'TICKET_ACTUALIZADO': return 'Actualización de tu ticket';
    case 'COTIZACION_SOLICITADA': return 'Solicitud de cotización';
    case 'PAYMENT_CONFIRMED': return 'Pago confirmado';
    case 'PAYMENT_REFERENCE': return 'Referencia de pago';
    case 'PAYMENT_FAILED': return 'Problema con un pago';
    case 'PAYMENT_REFUNDED': return 'Reembolso realizado';
    case 'PAYMENT_RELEASED': return 'Pago transferido';
    case 'PAYMENT_NO_CLABE': return 'Registra tu CLABE';
    case 'PAYMENT_ONBOARDING': return 'Conecta tu cuenta';
    case 'PAYMENT_CHARGEBACK': return 'Contracargo en revisión';
    case 'EXTRA_AGREGADO': return 'Extra agregado al servicio';
    case 'EXTRA_ELIMINADO': return 'Extra eliminado';
    default: return 'Notificación';
  }
};

const getSubtitle = (notif) => {
  switch (notif.type) {
    // Quién invita y a qué: el servicio va en la tarjeta de detalle. El servicio no se puede contratar hasta que la persona responda.
    case 'PAGE_INVITATION': return notif.data?.serviceName
      ? `${nombreDe(notif.sender) || 'Alguien'} te invita a impartir un servicio en su página. No se podrá contratar hasta que respondas.`
      : `${nombreDe(notif.sender) || 'Alguien'} te invita a formar parte de su página.`;
    // El servidor manda el texto completo (quién, qué servicio y qué pasa ahora); el de antes queda de respaldo
    case 'INVITATION_ACCEPTED': return notif.data?.message || `${notif.data?.userName || 'Alguien'} ha aceptado unirse a tu página.`;
    case 'INVITATION_REJECTED': return notif.data?.message || `${notif.data?.userName || 'Alguien'} ha declinado la invitación.`;
    case 'BOOKING_REQUEST': return `${notif.data?.userName} quiere reservar tu servicio.`;
    case 'BOOKING_CONFIRMED': return `${notif.data?.userName} confirmó tu reserva.`;
    case 'BOOKING_DECLINED': return `${notif.data?.userName} declinó tu solicitud.`;
    case 'BOOKING_CANCELLED': return `${notif.data?.userName} canceló su reserva.`;
    case 'BOOKING_MODIFICATION_REQUEST': return `${notif.data?.userName} quiere cambiar la fecha de la reserva.`;
    case 'BOOKING_MODIFICATION_ACCEPTED': return notif.data?.confirmada
      ? `${notif.data?.userName} aceptó tu propuesta de fecha y la reserva quedó confirmada.`
      : `${notif.data?.userName} aceptó tu solicitud de cambio de fecha.`;
    case 'BOOKING_MODIFICATION_REJECTED': return `${notif.data?.userName} rechazó tu solicitud de cambio de fecha.`;
    case 'BOOKING_MODIFIED': return `${notif.data?.userName} cambió la fecha de su reserva pendiente.`;
    case 'BOOKING_EXPIRED': return 'El proveedor no respondió a tiempo. Tu reserva fue cancelada automáticamente.';
    // Disputas, pagos y extras traen su texto en data.message
    default: return notif.data?.message || '';
  }
};

// Alguien pidió cotizar: abre esa conversación en el chat para responderle
const BotonResponderCotizacion = ({ notif }) => {
  const dispatch = useDispatch();
  if (!notif.sender?._id || !notif.data?.relatedId) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Button
        variant="contained"
        size="small"
        onClick={() => dispatch(startChat(notif.sender._id, notif.data.relatedId, notif.data.relatedType))}
        sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: 1, fontSize: '11px', '&:hover': { bgcolor: 'primary.dark' } }}
      >
        Responder
      </Button>
    </Box>
  );
};

const NotifItem = ({ notif, onAccept, onReject, navigate, isLast }) => (
  <>
    <Box sx={{ p: 2.5, bgcolor: notif.read ? 'transparent' : ((t) => alpha(t.palette.secondary.main, 0.04)) }}>
      <Stack direction="row" spacing={2}>
        <Avatar sx={{ bgcolor: getAvatarColor(notif.type), width: 44, height: 44, flexShrink: 0 }}>
          {getIcon(notif.type)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Typography variant="body2" fontWeight="bold" color="primary.main">
              {getTitle(notif.type)}
            </Typography>
            {!notif.read && (
              <Box sx={{ width: 8, height: 8, bgcolor: 'primary.main', borderRadius: '50%', mt: 0.5, ml: 1, flexShrink: 0 }} />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
            {getSubtitle(notif)}
          </Typography>

          {BOOKING_TYPES.includes(notif.type) && (
            <Box sx={{ bgcolor: 'sutil', p: 1.5, borderRadius: 1, mb: 1.5 }}>
              <Stack spacing={0.5}>
                <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                  <Box component="span" sx={{ color: 'text.secondary' }}>Servicio:</Box>
                  <Box component="span" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{notif.data?.serviceName}</Box>
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                  <Box component="span" sx={{ color: 'text.secondary' }}>Fecha:</Box>
                  <Box component="span" sx={{ color: 'primary.main' }}>{notif.data?.fecha}</Box>
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                  <Box component="span" sx={{ color: 'text.secondary' }}>Hora:</Box>
                  <Box component="span" sx={{ color: 'primary.main' }}>{notif.data?.hora} hrs</Box>
                </Typography>
                {notif.type === 'BOOKING_DECLINED' && notif.data?.motivo && (
                  <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>Motivo:</Box>
                    <Box component="span" sx={{ color: 'error.oscuro' }}>{notif.data.motivo}</Box>
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          {/* A qué página y servicio se refiere la invitación (también mientras está pendiente: hay que saber qué se acepta) */}
          {['PAGE_INVITATION', 'INVITATION_ACCEPTED', 'INVITATION_REJECTED'].includes(notif.type) && (
            <Box sx={{ bgcolor: 'sutil', p: 1.5, borderRadius: 1, mb: 1.5 }}>
              <Stack spacing={0.5}>
                <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                  <Box component="span" sx={{ color: 'text.secondary' }}>Página:</Box>
                  <Box component="span" sx={{ color: 'primary.main', fontWeight: 'bold' }}>{notif.data?.pageName}</Box>
                </Typography>
                {notif.data?.serviceName && (
                  <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>Servicio:</Box>
                    <Box component="span" sx={{ color: 'primary.main' }}>{notif.data.serviceName}</Box>
                  </Typography>
                )}
                {notif.type === 'PAGE_INVITATION' && notif.data?.modality && (
                  <Typography variant="caption" sx={{ display: 'flex', gap: 1 }}>
                    <Box component="span" sx={{ color: 'text.secondary' }}>Modalidad:</Box>
                    <Box component="span" sx={{ color: 'primary.main' }}>{notif.data.modality}</Box>
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          {(notif.type === 'BOOKING_REQUEST' || notif.type === 'BOOKING_MODIFICATION_REQUEST' || notif.type === 'BOOKING_MODIFIED') && (
            <Box sx={{ mb: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(rutaReserva(notif))}
                sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: 1, fontSize: '11px', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                {notif.type === 'BOOKING_MODIFIED' ? 'Ver reserva' : 'Ver solicitud'}
              </Button>
            </Box>
          )}

          {(notif.type === 'BOOKING_CONFIRMED' || notif.type === 'BOOKING_DECLINED' || notif.type === 'BOOKING_EXPIRED' || notif.type === 'BOOKING_CANCELLED' || notif.type === 'BOOKING_MODIFICATION_ACCEPTED' || notif.type === 'BOOKING_MODIFICATION_REJECTED') && (
            <Box sx={{ mb: 1 }}>
              <Chip
                label={
                  notif.type === 'BOOKING_CONFIRMED' ? 'CONFIRMADA'
                  : notif.type === 'BOOKING_DECLINED' ? 'DECLINADA'
                  : notif.type === 'BOOKING_CANCELLED' ? 'CANCELADA'
                  : notif.type === 'BOOKING_MODIFICATION_ACCEPTED' ? 'CAMBIO ACEPTADO'
                  : notif.type === 'BOOKING_MODIFICATION_REJECTED' ? 'CAMBIO RECHAZADO'
                  : 'CADUCADA'
                }
                size="small"
                sx={{
                  bgcolor: (notif.type === 'BOOKING_CONFIRMED' || notif.type === 'BOOKING_MODIFICATION_ACCEPTED') ? 'success.claro'
                    : (notif.type === 'BOOKING_DECLINED' || notif.type === 'BOOKING_CANCELLED' || notif.type === 'BOOKING_MODIFICATION_REJECTED') ? ((t) => alpha(t.palette.error.main, 0.15))
                    : 'sutil',
                  color: (notif.type === 'BOOKING_CONFIRMED' || notif.type === 'BOOKING_MODIFICATION_ACCEPTED') ? 'primary.main'
                    : (notif.type === 'BOOKING_DECLINED' || notif.type === 'BOOKING_CANCELLED' || notif.type === 'BOOKING_MODIFICATION_REJECTED') ? 'error.oscuro'
                    : 'text.secondary',
                  fontWeight: 'bold',
                  borderRadius: 1
                }}
              />
            </Box>
          )}

          {notif.type === 'PAGE_INVITATION' && notif.status === 'PENDING' ? (
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" size="small" onClick={() => onAccept(notif._id)}
                sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: 1, fontSize: '11px', '&:hover': { bgcolor: 'primary.dark' } }}>
                Aceptar
              </Button>
              <Button variant="contained" size="small"
                onClick={() => navigate('/user/colaboraciones')}
                sx={{ bgcolor: 'success.main', textTransform: 'none', borderRadius: 1, fontSize: '11px' }}>
                Ver detalles
              </Button>
              <Button variant="contained" color="error" size="small" onClick={() => onReject(notif._id)}
                sx={{ textTransform: 'none', borderRadius: 1, fontSize: '11px' }}>
                Rechazar
              </Button>
            </Box>
          ) : notif.type === 'PAGE_INVITATION' ? (
            <Box sx={{ mb: 1 }}>
              {/* CANCELLED: la invitación dejó de valer antes de responderla (se eligió a otra persona, se quitó el servicio…) */}
              <Chip
                label={notif.status === 'ACCEPTED' ? 'ACEPTADA' : notif.status === 'CANCELLED' ? 'YA NO DISPONIBLE' : 'RECHAZADA'}
                size="small"
                sx={{
                  bgcolor: notif.status === 'ACCEPTED' ? 'success.claro' : notif.status === 'CANCELLED' ? 'sutil' : ((t) => alpha(t.palette.error.main, 0.15)),
                  color: notif.status === 'ACCEPTED' ? 'primary.main' : notif.status === 'CANCELLED' ? 'text.secondary' : 'error.oscuro',
                  fontWeight: 'bold',
                  borderRadius: 1
                }}
              />
            </Box>
          ) : null}

          {/* Al dueño: si rechazaron (o dejaron) un servicio, va directo a la página a decidir quién lo imparte */}
          {notif.type === 'INVITATION_REJECTED' && notif.data?.pageId && (
            <Box sx={{ mb: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(`/user/crear-servicio/1?edit=${notif.data.pageId}&type=group`)}
                sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: 1, fontSize: '11px', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                Elegir quién lo imparte
              </Button>
            </Box>
          )}

          {notif.type === 'COTIZACION_SOLICITADA' && <BotonResponderCotizacion notif={notif} />}

          {TICKET_LINK_TYPES.includes(notif.type) && (
            <Box sx={{ mb: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(notif.data?.ticketId ? `/user/tickets?id=${notif.data.ticketId}` : '/user/tickets')}
                sx={{ bgcolor: 'primary.main', textTransform: 'none', borderRadius: 1, fontSize: '11px', '&:hover': { bgcolor: 'primary.dark' } }}
              >
                Ver ticket
              </Button>
            </Box>
          )}

          <BotonAlLugar notif={notif} navigate={navigate} />

          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            hace {moment(notif.date).fromNow(true)}
          </Typography>
        </Box>
      </Stack>
    </Box>
    {!isLast && <Divider />}
  </>
);

const SeccionNotificaciones = ({ titulo, subtitulo, notifs, onAccept, onReject, navigate, accion }) => (
  <Box sx={{ mb: 4 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
      <Box>
        <Typography variant="h6" fontWeight="bold" color="primary.main">{titulo}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{subtitulo}</Typography>
      </Box>
      {accion}
    </Box>
    {notifs.length === 0 ? (
      <Box sx={{ bgcolor: 'white', borderRadius: 2, p: 4, textAlign: 'center', border: 1, borderColor: 'sutil' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>Sin notificaciones en esta sección</Typography>
      </Box>
    ) : (
      <Box sx={{ bgcolor: 'white', borderRadius: 2, border: 1, borderColor: 'sutil', overflow: 'hidden' }}>
        {notifs.map((notif, i) => (
          <NotifItem
            key={notif._id}
            notif={notif}
            onAccept={onAccept}
            onReject={onReject}
            navigate={navigate}
            isLast={i === notifs.length - 1}
          />
        ))}
      </Box>
    )}
  </Box>
);

const NotificacionesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { notifications } = useSelector(state => state.notifications);
  const [fechaCarga] = useState(moment().format('DD/MM/YYYY HH:mm'));

  useEffect(() => {
    dispatch(getNotifications());
  }, [dispatch]);

  const nuevas = (notifications || []).filter(n => !n.read);
  const recientes = (notifications || []).filter(n => n.read);

  // Resultado de responder una invitación: se confirma lo que pasó (o por qué no se pudo)
  const [aviso, setAviso] = useState(null);

  const responder = async (id, accion) => {
    const res = await dispatch(handleInvitation(id, accion));
    setAviso(res.ok
      ? { tipo: 'success', texto: accion === 'ACCEPT' ? 'Aceptaste la invitación. Ya apareces como quien imparte el servicio.' : 'Rechazaste la invitación. Se le avisó a quien te invitó.' }
      : { tipo: 'error', texto: res.msg });
  };

  const onAccept = (id) => responder(id, 'ACCEPT');

  const onReject = (id) => responder(id, 'REJECT');

  const onMarcarTodas = () => {
    dispatch(markAllAsRead());
  };

  return (
    <Box className="main-page">
      <Box sx={{ bgcolor: 'secondary.main', color: 'primary.main', borderRadius: 2, px: 4, py: 5, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">Centro de notificaciones</Typography>
          <Typography mt={1} variant="body2">Conoce tus notificaciones</Typography>
        </Box>
        <Typography variant="caption" sx={{ mt: 0.5 }}>
          Fecha y hora de ultima actualización de datos: {fechaCarga} h
        </Typography>
      </Box>

      {aviso && (
        <Alert severity={aviso.tipo} onClose={() => setAviso(null)} sx={{ mb: 2, borderRadius: '12px' }}>
          {aviso.texto}
        </Alert>
      )}

      <SeccionNotificaciones
        titulo="Nuevas"
        subtitulo="Consulta tus nuevas notificaciones"
        notifs={nuevas}
        onAccept={onAccept}
        onReject={onReject}
        navigate={navigate}
        accion={nuevas.length > 0 && (
          <Button
            variant="text"
            size="small"
            onClick={onMarcarTodas}
            sx={{ textTransform: 'none', color: 'primary.main', fontWeight: 'bold', fontSize: '12px' }}
          >
            Marcar todas como leídas
          </Button>
        )}
      />

      <SeccionNotificaciones
        titulo="Recientes"
        subtitulo="Consulta otras notificaciones"
        notifs={recientes}
        onAccept={onAccept}
        onReject={onReject}
        navigate={navigate}
      />
    </Box>
  );
};

export default NotificacionesPage;
